/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import classNames from 'classnames'
import Image from 'next/image'
import type { StructuredText } from 'datocms-structured-text-utils'
import BlogContent from './content'
import { FadeInSection } from 'components/materia/sub-blocks/fade-in-wrapper'
import { Header, IHeader } from './header'
import { Metadata, type IMetadata } from './metadata'
import { TableOfContents } from 'components/materia/sub-blocks/table-of-contents'
import { getHeadingsFromStContent } from 'components/materia/sub-blocks/table-of-contents/get-headings-from-st-content'
import s_lfc from 'components/longform-content-section/style.module.css'
import s from './post.module.css'

export interface IBlogPost {
	header: IHeader
	image: {
		src: string
		alt: string
		width: number
		height: number
	}
	category: string
	content: StructuredText
	subtitle?: IMetadata['subtitle']
	authors: IMetadata['authors']
	date: IMetadata['date']
}

export default function BlogPost({
	image,
	header,
	content,
	authors,
	subtitle,
	date,
}: IBlogPost) {
	const headings = getHeadingsFromStContent(content)

	return (
		<div className={classNames(s.root, 'mds-dark-mode')}>
			<div className={s.header}>
				<Header {...header} />
			</div>
			<div className={s.image}>
				<Image src={image.src} alt={image.alt} {...image} />
			</div>
			<div className={s.leftColumn}>
				<div className={s.stickyColumn}>
					<FadeInSection>
						<Metadata date={date} authors={authors} subtitle={subtitle} />
					</FadeInSection>
				</div>
			</div>
			<div className={s.content}>
				<BlogContent
					className={classNames(s.content, s_lfc.longformContentSection)}
					content={content}
				/>
			</div>
			<div className={s.rightColumn}>
				<div className={s.stickyColumn}>
					{headings && headings?.length ? (
						<FadeInSection>
							<TableOfContents headings={headings} />
						</FadeInSection>
					) : null}
				</div>
			</div>
		</div>
	)
}
